import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  IndianRupee, TrendingUp, DollarSign, Wallet,
  ArrowDownCircle, ArrowUpCircle, RefreshCw, X, Trophy,
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const TYPE_LABELS = {
  booking_payment:           { label: 'Booking Received',     color: '#16a34a', bg: '#DCEFB8', dir: 'in' },
  credit:                    { label: 'Credit',                color: '#16a34a', bg: '#DCEFB8', dir: 'in' },
  debit:                     { label: 'Payout / Refund Out',   color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  withdrawal:                { label: 'Withdrawal',            color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  refund:                    { label: 'Refund',                color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  platform_fee_credit:       { label: 'Platform Fee',          color: '#2563eb', bg: '#eff6ff', dir: 'in' },
  tournament_platform_fee:   { label: '🏆 Tournament Fee',      color: '#8b5cf6', bg: '#f3e8ff', dir: 'in' },
  tournament_earning:        { label: '🏆 Tournament Earning',  color: '#16a34a', bg: '#DCEFB8', dir: 'in' },
  tournament_payment:        { label: '🏆 Tournament Payment',  color: '#8b5cf6', bg: '#f3e8ff', dir: 'in' },
  partner_fee_debit:         { label: 'Partner Fee Debit',     color: '#9333ea', bg: '#f3e8ff', dir: 'out' },
  cancellation_refund:          { label: 'Cancellation Refund',   color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  cancellation_compensation:    { label: 'Cancel Compensation',   color: '#f59e0b', bg: '#fef3c7', dir: 'out' },
  partner_payout:               { label: 'Partner Payout',        color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  partner_payout_debit:         { label: 'Partner Payout',        color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  cancellation_refund_debit:    { label: 'Cancellation Refund',   color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  cancellation_compensation_debit: { label: 'Cancel Compensation', color: '#f59e0b', bg: '#fef3c7', dir: 'out' },
  partner_payout_reversal_credit: { label: 'Payout Reversal',  color: '#16a34a', bg: '#DCEFB8', dir: 'in' },
};

export default function AdminWallet() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmt, setWithdrawAmt] = useState('');
  const [bankDetails, setBankDetails] = useState({ accountNumber: '', ifscCode: '', accountHolderName: '', bankName: '' });
  const [processing, setProcessing] = useState(false);
  const [activeSection, setActiveSection] = useState('transactions'); // transactions | settlements
  const [selectedTxn, setSelectedTxn] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/admin/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmt);
    if (!amt || amt <= 0) return alert('Enter a valid amount');
    if (amt > (data?.balance || 0)) return alert('Insufficient balance');
    if (!bankDetails.accountNumber || !bankDetails.ifscCode || !bankDetails.accountHolderName)
      return alert('Please fill all bank details');

    if (bankDetails.accountNumber) {
      const cleanAcc = bankDetails.accountNumber.trim();
      if (!/^\d{9,18}$/.test(cleanAcc)) {
        return alert('Bank account number must be between 9 and 18 digits.');
      }
    }
    if (bankDetails.ifscCode) {
      const cleanIfsc = bankDetails.ifscCode.trim();
      if (!/^[A-Za-z0-9]{6,14}$/.test(cleanIfsc)) {
        return alert('IFSC code must be 6 to 14 alphanumeric characters.');
      }
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/wallet/withdraw`,
        { amount: amt, bankAccount: bankDetails },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const msg = res.data?.msg || 'Withdrawal request submitted! You will receive your requested amount to your account through UPI within 2 business days.';
      alert(msg);
      setShowWithdraw(false);
      setWithdrawAmt('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.msg || 'Withdrawal failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '700' }}>
      Loading wallet data...
    </div>
  );

  const balance = data?.balance || 0;
  const totalCollected = data?.totalCollected || 0;
  const totalDisbursed = data?.totalDisbursed || 0;

  const transactions = (data?.transactions || []).filter(t => {
    if (filter === 'in')  return ['booking_payment','credit','platform_fee_credit','partner_payout_reversal_credit','tournament_platform_fee','tournament_earning','tournament_payment'].includes(t.type);
    if (filter === 'out') return ['debit','withdrawal','refund','cancellation_refund','cancellation_compensation','partner_payout','partner_fee_debit','cancellation_refund_debit','partner_payout_debit','cancellation_compensation_debit'].includes(t.type);
    return true;
  });

  const settlements = data?.settlements || [];

  const pendingPayouts = (data?.transactions || [])
    .filter(t => t.type === 'partner_payout_debit' && t.status === 'pending')
    .reduce((sum, t) => sum + t.amount, 0);

  const tournamentFees = data?.tournamentFees ?? (data?.transactions || [])
    .filter(t => (t.type === 'tournament_platform_fee' || t.type === 'tournament_earning' || t.type === 'tournament_payment') && t.status === 'success')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const statCards = [
    { 
      label: 'Admin Wallet Balance', 
      value: `₹${(balance - pendingPayouts).toLocaleString()}`, 
      icon: <Wallet size={20} />, 
      color: '#CEF17B', 
      sub: `₹${balance.toLocaleString()} (Total) - ₹${pendingPayouts.toLocaleString()} (Paying to Vendor) = ₹${(balance - pendingPayouts).toLocaleString()} (Remaining)` 
    },
    { label: 'Total Collected', value: `₹${totalCollected.toLocaleString()}`, icon: <ArrowDownCircle size={20} />, color: '#3b82f6', sub: 'From all bookings & tournaments' },
    { label: '🏆 Tournament Fees', value: `₹${tournamentFees.toLocaleString()}`, icon: <Trophy size={20} />, color: '#8b5cf6', sub: 'Platform tournament revenue' },
    { label: 'Total Deducted', value: `₹${totalDisbursed.toLocaleString()}`, icon: <ArrowUpCircle size={20} />, color: '#dc2626', sub: 'Payouts + refunds' },
    { label: 'Net Retained', value: `₹${(totalCollected - totalDisbursed).toLocaleString()}`, icon: <TrendingUp size={20} />, color: '#10b981', sub: 'Platform earnings' },
  ];

  return (
    <div style={{ padding: '0.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#161616', marginBottom: '4px' }}>Admin Wallet</h2>
          <p style={{ color: '#374151', fontSize: '0.9rem', fontWeight: '600' }}>
            Centralized payment hub — all booking money flows here first
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchData} style={btnSecondary}><RefreshCw size={16} /> Refresh</button>
          <button onClick={() => setShowWithdraw(true)} style={btnPrimary}><ArrowDownCircle size={16} /> Withdraw Funds</button>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {statCards.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151' }}>{s.label}</span>
              <div style={{ background: `${s.color}20`, color: s.color, padding: '6px', borderRadius: '8px' }}>{s.icon}</div>
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#161616', marginBottom: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '600' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
        {[
          { id: 'transactions', label: `Transactions (${data?.transactions?.length || 0})` },
          { id: 'settlements',  label: `Settlements (${settlements.length})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveSection(t.id)}
            style={{
              padding: '8px 20px', borderRadius: '10px', border: '1.5px solid',
              borderColor: activeSection === t.id ? '#084734' : '#EEF2E6',
              background: activeSection === t.id ? '#084734' : 'white',
              color: activeSection === t.id ? '#CEF17B' : '#374151',
              fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}

        {activeSection === 'transactions' && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
            {[['all','All'],['in','Inflow'],['out','Outflow']].map(([v,l]) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                style={{
                  padding: '6px 14px', borderRadius: '8px', fontSize: '0.78rem',
                  fontWeight: '700', cursor: 'pointer', border: '1.5px solid',
                  borderColor: filter === v ? '#084734' : '#EEF2E6',
                  background: filter === v ? '#DCEFB8' : 'white',
                  color: filter === v ? '#084734' : '#374151',
                }}
              >{l}</button>
            ))}
          </div>
        )}
      </div>

      {/* Transactions Table */}
      {activeSection === 'transactions' && (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead style={{ background: '#F8FAF7' }}>
                <tr>
                  {['Date', 'Type', 'Description', 'Venue', 'Customer', 'Amount', 'Status'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No transactions yet</td></tr>
                ) : transactions.map((t, i) => {
                  const meta = TYPE_LABELS[t.type] || { label: t.type, color: '#374151', bg: '#F8FAF7', dir: 'in' };
                  return (
                    <tr 
                      key={t.id} 
                      onClick={() => setSelectedTxn(t)} 
                      style={{ 
                        borderBottom: '1px solid #EEF2E6', 
                        background: i % 2 === 0 ? 'white' : '#FAFFFE',
                        cursor: 'pointer' 
                      }}
                    >
                      <td style={tdStyle}>
                        <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0f172a' }}>
                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#374151' }}>
                          {t.createdAt ? new Date(t.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ background: meta.bg, color: meta.color, padding: '3px 10px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '800' }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '220px' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.description}
                        </div>
                        {t.bookingRef && (
                          <div style={{ fontSize: '0.7rem', color: '#98A2B3', fontWeight: '700', marginTop: '2px' }}>
                            #{t.bookingRef}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '0.82rem', color: '#374151' }}>{t.turfName || '—'}</td>
                      <td style={{ ...tdStyle, fontSize: '0.82rem', color: '#374151' }}>{t.userName || '—'}</td>
                      <td style={tdStyle}>
                        <div style={{
                          fontWeight: '900', fontSize: '0.95rem',
                          color: meta.dir === 'in' ? '#16a34a' : '#dc2626',
                        }}>
                          {meta.dir === 'in' ? '+' : '-'}₹{t.amount.toLocaleString()}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          background: t.status === 'success' ? '#D1FAE5' : t.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                          color: t.status === 'success' ? '#065F46' : t.status === 'pending' ? '#92400E' : '#DC2626',
                          padding: '3px 10px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800',
                        }}>
                          {(t.status || 'success').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Settlements Table */}
      {activeSection === 'settlements' && (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead style={{ background: '#F8FAF7' }}>
                <tr>
                  {['Settled At', 'Booking', 'Partner', 'Base Amt', 'Partner Fee', 'Partner GST', 'Payout', 'Type', 'Status'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {settlements.length === 0 ? (
                  <tr><td colSpan="9" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No settlements yet</td></tr>
                ) : settlements.map((s, i) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #EEF2E6' }}>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '0.82rem', fontWeight: '700' }}>
                        {s.settledAt ? new Date(s.settledAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.82rem', fontFamily: 'monospace', color: '#084734', fontWeight: '700' }}>
                      #{s.bookingRef || '—'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '0.82rem', fontWeight: '600' }}>{s.partnerName || '—'}</td>
                    <td style={{ ...tdStyle, fontWeight: '700' }}>₹{s.baseAmount?.toLocaleString()}</td>
                    <td style={{ ...tdStyle, color: '#8b5cf6', fontWeight: '700' }}>₹{s.partnerPlatformFee?.toLocaleString()}</td>
                    <td style={{ ...tdStyle, color: '#f59e0b', fontWeight: '700' }}>₹{s.partnerGst?.toLocaleString()}</td>
                    <td style={{ ...tdStyle, color: '#16a34a', fontWeight: '900' }}>₹{s.payoutAmount?.toLocaleString()}</td>
                    <td style={tdStyle}>
                      <span style={{
                        background: s.type === 'booking_payout' ? '#DCEFB8' : '#FEF3C7',
                        color: s.type === 'booking_payout' ? '#084734' : '#92400E',
                        padding: '3px 8px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800',
                      }}>
                        {s.type === 'booking_payout' ? 'PAYOUT' : 'COMPENSATION'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        background: s.status === 'completed' ? '#D1FAE5' : '#FEF3C7',
                        color: s.status === 'completed' ? '#065F46' : '#92400E',
                        padding: '3px 8px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800',
                      }}>
                        {(s.status || '').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.2rem', color: '#161616' }}>Withdraw Admin Funds</h3>
              <button onClick={() => setShowWithdraw(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ background: '#DCEFB8', borderRadius: '10px', padding: '12px', marginBottom: '1.25rem' }}>
              <p style={{ fontSize: '0.85rem', color: '#084734', fontWeight: '700', margin: 0 }}>
                Available Balance: <strong>₹{balance.toLocaleString()}</strong>
              </p>
            </div>
            {[
              { label: 'Amount (₹)', key: 'amount', placeholder: 'Enter amount', type: 'number' },
              { label: 'Account Holder Name', key: 'accountHolderName', placeholder: 'As per bank records' },
              { label: 'Account Number', key: 'accountNumber', placeholder: 'Account number' },
              { label: 'IFSC Code', key: 'ifscCode', placeholder: 'IFSC code' },
              { label: 'Bank Name (optional)', key: 'bankName', placeholder: 'Bank name' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '6px' }}>{f.label}</label>
                <input
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={f.key === 'amount' ? withdrawAmt : bankDetails[f.key]}
                  onChange={e => f.key === 'amount' ? setWithdrawAmt(e.target.value) : setBankDetails({ ...bankDetails, [f.key]: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #EEF2E6', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem' }}>
              <button onClick={handleWithdraw} disabled={processing} style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }}>
                {processing ? 'Processing...' : 'Submit Withdrawal'}
              </button>
              <button onClick={() => setShowWithdraw(false)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTxn && (
        <div style={modalOverlay} onClick={() => setSelectedTxn(null)}>
          <div style={{ ...modalBox, width: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '0.75rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.2rem', color: '#161616', margin: 0 }}>Transaction Details</h3>
              <button onClick={() => setSelectedTxn(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151' }}><X size={20} /></button>
            </div>
            
            <div style={{ textAlign: 'center', padding: '1.25rem', background: '#F8FAF7', borderRadius: '16px', marginBottom: '1.5rem', border: '1px solid #EEF2E6' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase', color: '#374151', marginBottom: '4px', letterSpacing: '0.5px' }}>
                {TYPE_LABELS[selectedTxn.type]?.label || selectedTxn.type}
              </div>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '900', 
                color: (TYPE_LABELS[selectedTxn.type]?.dir === 'in') ? '#16a34a' : '#dc2626' 
              }}>
                {(TYPE_LABELS[selectedTxn.type]?.dir === 'in' ? '+' : '-')}₹{selectedTxn.amount.toLocaleString()}
              </div>
              <div style={{ marginTop: '10px' }}>
                <span style={{
                  background: selectedTxn.status === 'success' ? '#D1FAE5' : selectedTxn.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                  color: selectedTxn.status === 'success' ? '#065F46' : selectedTxn.status === 'pending' ? '#92400E' : '#DC2626',
                  padding: '4px 12px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800',
                }}>
                  {(selectedTxn.status || 'success').toUpperCase()}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              <div style={detailRow}>
                <span style={detailLabel}>Description</span>
                <span style={detailValue}>{selectedTxn.description}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Date & Time</span>
                <span style={detailValue}>
                  {selectedTxn.createdAt ? new Date(selectedTxn.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Internal Transaction ID</span>
                <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem' }}>{selectedTxn.id}</span>
              </div>
              
              {selectedTxn.bookingRef && (
                <div style={detailRow}>
                  <span style={detailLabel}>Booking Reference</span>
                  <span style={{ ...detailValue, fontWeight: '700', color: '#084734' }}>#{selectedTxn.bookingRef}</span>
                </div>
              )}

              {selectedTxn.turfName && (
                <div style={detailRow}>
                  <span style={detailLabel}>Venue (Turf)</span>
                  <span style={{ ...detailValue, fontWeight: '700' }}>{selectedTxn.turfName}</span>
                </div>
              )}

              {selectedTxn.userName && (
                <div style={detailRow}>
                  <span style={detailLabel}>Customer</span>
                  <span style={detailValue}>{selectedTxn.userName}</span>
                </div>
              )}

              {(selectedTxn.razorpayPaymentId || selectedTxn.paymentId || selectedTxn.payuMihpayid || selectedTxn.payu_mihpayid || selectedTxn.metadata?.payu_mihpayid) && (
                <div style={detailRow}>
                  <span style={detailLabel}>PayU ID (mihpayid)</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem', color: '#2563eb' }}>
                    {selectedTxn.payuMihpayid || selectedTxn.payu_mihpayid || selectedTxn.metadata?.payu_mihpayid || selectedTxn.razorpayPaymentId || selectedTxn.paymentId}
                  </span>
                </div>
              )}

              {(selectedTxn.razorpayOrderId || selectedTxn.payuTxnId || selectedTxn.payu_txnid || selectedTxn.metadata?.payu_txnid) && (
                <div style={detailRow}>
                  <span style={detailLabel}>PayU Txn ID (txnid)</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {selectedTxn.payuTxnId || selectedTxn.payu_txnid || selectedTxn.metadata?.payu_txnid || selectedTxn.razorpayOrderId}
                  </span>
                </div>
              )}

              {(selectedTxn.bankRefNum || selectedTxn.bank_ref_num || selectedTxn.metadata?.bank_ref_num) && (
                <div style={detailRow}>
                  <span style={detailLabel}>Bank Reference Num</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem', color: '#059669' }}>
                    {selectedTxn.bankRefNum || selectedTxn.bank_ref_num || selectedTxn.metadata?.bank_ref_num}
                  </span>
                </div>
              )}

              {(selectedTxn.paymentMode || selectedTxn.payment_mode || selectedTxn.metadata?.payment_mode) && (
                <div style={detailRow}>
                  <span style={detailLabel}>Payment Mode</span>
                  <span style={{ ...detailValue, fontWeight: '700' }}>
                    {selectedTxn.paymentMode || selectedTxn.payment_mode || selectedTxn.metadata?.payment_mode}
                  </span>
                </div>
              )}

              {(selectedTxn.upiId || selectedTxn.upi_id || selectedTxn.metadata?.upi_id) && (
                <div style={detailRow}>
                  <span style={detailLabel}>UPI ID / VPA</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem', color: '#7c3aed' }}>
                    {selectedTxn.upiId || selectedTxn.upi_id || selectedTxn.metadata?.upi_id}
                  </span>
                </div>
              )}

              {selectedTxn.metadata && Object.keys(selectedTxn.metadata).length > 0 && (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid #EEF2E6' }}>
                  <div style={{ fontWeight: '800', fontSize: '0.8rem', color: '#161616', marginBottom: '0.5rem' }}>Transaction Metadata</div>
                  <div style={{ background: '#F8FAF7', borderRadius: '10px', padding: '10px', fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {Object.entries(selectedTxn.metadata).map(([key, val]) => (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#374151', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span style={{ fontWeight: '700', color: '#161616' }}>
                          {(typeof val === 'number' && (key.toLowerCase().includes('amount') || key.toLowerCase().includes('fee') || key.toLowerCase().includes('payout') || key.toLowerCase().includes('gst') || key.toLowerCase().includes('paid') || key.toLowerCase().includes('earnings') || key.toLowerCase().includes('entry'))) ? `₹${val}` : String(val)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem' }}>
              {selectedTxn.status === 'pending' && (
                <button 
                  onClick={async () => {
                    if (!window.confirm("Mark this pending transaction as successful? This indicates the funds have been successfully transferred to the user/partner.")) return;
                    try {
                      await axios.post(`${API}/wallet/admin/transactions/${selectedTxn.id}/success`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      alert("Transaction marked as successful.");
                      setSelectedTxn(null);
                      fetchData();
                    } catch (err) {
                      alert(err.response?.data?.msg || "Failed to update transaction status.");
                    }
                  }}
                  style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }}
                >
                  Mark as Success
                </button>
              )}
              <button onClick={() => setSelectedTxn(null)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = {
  background: 'white', borderRadius: '16px', padding: '1.25rem',
  border: '1.5px solid #EEF2E6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
};
const btnPrimary = {
  background: '#CEF17B', color: '#084734', border: 'none', padding: '10px 20px',
  borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem',
  display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
};
const btnSecondary = {
  background: 'white', color: '#374151', border: '1.5px solid #EEF2E6', padding: '10px 20px',
  borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem',
  display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
};
const thStyle = {
  textAlign: 'left', padding: '12px 14px', fontSize: '0.72rem',
  fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px',
};
const tdStyle = { padding: '12px 14px', verticalAlign: 'middle' };
const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalBox = {
  background: 'white', borderRadius: '20px', padding: '2rem',
  width: '480px', maxWidth: '95%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  maxHeight: '90vh', overflowY: 'auto',
};
const detailRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 0',
  borderBottom: '1px solid #F3F4F6',
};
const detailLabel = {
  color: '#374151',
  fontWeight: '600',
};
const detailValue = {
  color: '#161616',
  fontWeight: '700',
  textAlign: 'right',
};
